import {
  QueryClient,
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  UserSegmentEnum,
  BookmarkTypeEnum,
  GeneralFeedbackTypeEnum,
  ModuleI,
  ModuleStatus,
  LessonI,
  RecapI,
  ResponseI,
  BookmarkI,
  ToolboxI,
  ResponsesGroupedByLessonI,
  ToolboxProfileDefinitionI,
  ToolboxCheckinDefinitionI,
  ToolboxPlanI,
  UserToolboxProfileI,
} from "../types";
import { APIError } from "../utils/ErrorHandling";
import { GroupedPageBookmarkLessonI } from "../pages/Toolbox/components/FavoritesListGrouped";
import { useAuthStore } from "../stores/useAuthStore";
import * as client from "./client";
import * as rbacClient from "./rbac";
import {
  LegalComplianceResponse,
  TutorialStatusResponse,
  AnnouncementStatusFilter,
  AnnouncementFilters,
} from "./types";

export function useGetTopics(
  title?: string,
  status?: ModuleStatus,
  options?: Pick<UseQueryOptions<ModuleI[], APIError>, "enabled">,
) {
  return useQuery<ModuleI[], APIError>({
    queryKey: ["modules", title, status],
    queryFn: () => client.getTopics(title, status),
    ...options,
  });
}

export function useGetTopic(id: string) {
  return useQuery<ModuleI, APIError>({
    queryKey: ["modules", id],
    queryFn: () => client.getTopic(id),
    enabled: !!id,
  });
}

export function useGetLessons(title?: string) {
  return useQuery({
    queryKey: ["lessons", title],
    queryFn: () => client.getLessons(title),
  });
}

export function useGetLesson(id: string) {
  return useQuery<LessonI, APIError>({
    queryKey: ["lessons", id],
    queryFn: () => client.getLesson(id),
    enabled: !!id,
  });
}

export function useGetRecaps(lessonId?: string) {
  return useQuery<RecapI[], APIError>({
    queryKey: ["recaps"],
    queryFn: () => client.getRecaps(lessonId),
  });
}

export function useGetPage(id: string) {
  return useQuery({
    queryKey: ["pages", id],
    queryFn: () => client.getPage(id),
  });
}

export function useGetPages(title?: string) {
  return useQuery({
    queryKey: ["pages", title],
    queryFn: () => client.getPages(title),
  });
}

export function useGetUnlinkedLessons() {
  return useQuery<LessonI[], APIError>({
    queryKey: ["lessons", "unlinked"],
    queryFn: () => client.getUnlinkedLessons(),
  });
}

export function useGetUnlinkedPages() {
  return useQuery({
    queryKey: ["pages", "unlinked"],
    queryFn: () => client.getUnlinkedPages(),
  });
}

export function useGetDeletedPages() {
  return useQuery({
    queryKey: ["pages", "deleted"],
    queryFn: () => client.getDeletedPages(),
  });
}

export function useGetArchivedPages() {
  return useQuery({
    queryKey: ["pages", "archived"],
    queryFn: () => client.getArchivedPages(),
  });
}

export function useGetImages(imageType?: string) {
  return useQuery({
    queryKey: ["images", imageType],
    queryFn: () => client.getImages(imageType),
  });
}

export function useGetImageById(imageId: string) {
  return useQuery({
    queryKey: ["images", imageId],
    queryFn: () => client.getImageById(imageId),
    enabled: !!imageId,
  });
}

export function useGetImagesPaginated(
  imageType?: string,
  q?: string,
  page?: number,
  limit?: number,
  extension?: string,
) {
  return useQuery({
    queryKey: ["images", imageType, q, page, limit, extension],
    queryFn: () =>
      client.getImagesPaginated(imageType, q, page, limit, extension || ""),
  });
}

export function useGetResponses() {
  return useQuery({
    queryKey: ["responses"],
    queryFn: () => client.getResponses(),
  });
}

export function useGetResponsesGrouped(moduleId?: string) {
  return useQuery<ResponsesGroupedByLessonI[], APIError>({
    queryKey: ["responses", "grouped", moduleId],
    queryFn: () => client.getResponsesGrouped(moduleId),
  });
}

export function useGetResponse(id: string) {
  return useQuery({
    queryKey: ["responses", id],
    queryFn: () => client.getResponse(id),
  });
}

export function useGetResponsesByPages(pageIds: string[]) {
  return useQuery<Record<string, ResponseI[]>, APIError>({
    queryKey: ["responses", "pages", pageIds],
    queryFn: () => client.getResponsesByPages(pageIds),
    enabled: pageIds.length > 0,
  });
}

export function useGetPageBookmarks(
  pageId?: string,
  moduleId?: string,
  options?: { enabled?: boolean },
) {
  return useQuery<BookmarkI[], APIError>({
    queryKey: ["bookmarks", BookmarkTypeEnum.PAGE, pageId, moduleId],
    queryFn: () => client.getBookmarks(BookmarkTypeEnum.PAGE, moduleId, pageId),
    ...options,
  });
}

export function useGetPageBookmarksGroupedLessons(moduleId?: string) {
  return useQuery<GroupedPageBookmarkLessonI[], APIError>({
    queryKey: [
      "bookmarks",
      BookmarkTypeEnum.PAGE,
      "grouped",
      "lessons",
      moduleId,
    ],
    queryFn: () => client.getPageBookmarksGroupedLessons(moduleId),
  });
}

export function useGetTopicBookmarks(moduleId?: string) {
  return useQuery<BookmarkI[], APIError>({
    queryKey: ["bookmarks", BookmarkTypeEnum.MODULE, moduleId],
    queryFn: () => client.getBookmarks(BookmarkTypeEnum.MODULE, moduleId),
  });
}

export function useGetLessonViews(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: ["analytics", "lesson", segment, startDate, endDate],
    queryFn: () => client.getLessonViews(segment, startDate, endDate),
  });
}

export function useGetTopicViews(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: ["analytics", "module", segment, startDate, endDate],
    queryFn: () => client.getTopicViews(segment, startDate, endDate),
  });
}

export function useGetLessonViewsByTopicId(
  moduleId: string,
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [
      "analytics",
      "lesson",
      "by-module",
      moduleId,
      segment,
      startDate,
      endDate,
    ],
    queryFn: () =>
      client.getLessonViewsByTopicId(moduleId, segment, startDate, endDate),
    enabled: !!moduleId && (options?.enabled ?? true),
  });
}

export function useGetPageViewsByTopicAndLesson(
  moduleId: string,
  lessonId: string,
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [
      "analytics",
      "page",
      "by-module-lesson",
      moduleId,
      lessonId,
      segment,
      startDate,
      endDate,
    ],
    queryFn: () =>
      client.getPageViewsByTopicAndLesson(
        moduleId,
        lessonId,
        segment,
        startDate,
        endDate,
      ),
    enabled: !!moduleId && !!lessonId && (options?.enabled ?? true),
  });
}

export function useGetLessonCompletedCount(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: ["analytics", "lesson", "completed", segment, startDate, endDate],
    queryFn: () => client.getLessonCompletedCount(segment, startDate, endDate),
  });
}

export function useGetPageContainedLessons(
  pageId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ["page", "used", pageId],
    queryFn: () => client.getPageContainedLessons(pageId),
    enabled: options?.enabled ?? true,
  });
}

export function useGetLessonContainedTopics(lessonId: string) {
  return useQuery({
    queryKey: ["lesson", "used", "modules", lessonId],
    queryFn: () => client.getLessonsContainedTopics(lessonId),
  });
}

export function useGetLessonFeedbackById(
  lessonId: string,
  segments: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: ["lessonFeedback", lessonId, segments, startDate, endDate],
    queryFn: () =>
      client.getLessonFeedbackById(lessonId, segments, startDate, endDate),
    enabled: !!lessonId,
  });
}

export function useGetLessonRatings(
  segments: UserSegmentEnum[],
  moduleId?: string,
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: ["lessonRatings", segments, moduleId || "", startDate, endDate],
    queryFn: () =>
      client.getLessonRatings(
        segments,
        moduleId || undefined,
        startDate,
        endDate,
      ),
  });
}

const FEEDBACK_PAGE_SIZE = 50;

export function useGetFeedbackAnalyticsSummary(
  segments: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
  feedbackType?: string,
  page: number = 1,
  limit: number = FEEDBACK_PAGE_SIZE,
  moduleId?: string,
  lessonId?: string,
) {
  return useQuery({
    queryKey: [
      "feedbackAnalyticsSummary",
      segments,
      startDate,
      endDate,
      feedbackType,
      page,
      limit,
      moduleId,
      lessonId,
    ],
    queryFn: () =>
      client.getFeedbackAnalyticsSummary({
        segments,
        startDate,
        endDate,
        feedbackType: feedbackType
          ? (feedbackType as GeneralFeedbackTypeEnum)
          : undefined,
        page,
        limit,
        moduleId: moduleId || undefined,
        lessonId: lessonId || undefined,
      }),
  });
}

export function useGetUserPreferences() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return useQuery({
    queryKey: ["userPreferences"],
    queryFn: () => client.getUserPreferences(),
    enabled: isAuthenticated,
    select: (data) => ({
      saveAnswersToLog: data?.saveAnswersToLog ?? true,
      askEveryTime: data?.askEveryTime ?? false,
      dismissedTutorials: data?.dismissedTutorials ?? [],
      emailConsentDate: data?.emailConsentDate,
      emailAccountIssues: data?.emailAccountIssues,
      emailFeedbackRequests: data?.emailFeedbackRequests,
      emailNewsUpdates: data?.emailNewsUpdates,
      videoPlaybackSpeed: data?.videoPlaybackSpeed ?? 1,
      autoplayVideos: data?.autoplayVideos ?? false,
      videoVolume: data?.videoVolume ?? 1, // Default to full volume
    }),
  });
}

export function useGetToolboxByTopicId(moduleId: string) {
  return useQuery<ToolboxI | null, APIError>({
    queryKey: ["toolbox", moduleId],
    queryFn: async () => {
      try {
        return await client.getToolbox(moduleId);
      } catch (error) {
        // If toolbox doesn't exist (404), return null instead of throwing
        if ((error as APIError)?.statusCode === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!moduleId,
    retry: (_, error) =>
      !(error instanceof APIError && error.statusCode === 404),
  });
}

export function useGetActiveToolboxes() {
  return useQuery<{ activeToolboxes: ModuleI[]; count: number }, APIError>({
    queryKey: ["activeToolboxes"],
    queryFn: () => client.getActiveToolboxes(),
  });
}

export function useGetLoginFrequency(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: [
      "analytics",
      "auth",
      "login-frequency",
      segment,
      startDate,
      endDate,
    ],
    queryFn: () => client.getLoginFrequency(segment, startDate, endDate),
  });
}

export function useGetLoginCountDistribution(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: [
      "analytics",
      "auth",
      "login-count-distribution",
      segment,
      startDate,
      endDate,
    ],
    queryFn: () =>
      client.getLoginCountDistribution(segment, startDate, endDate),
  });
}

export function useGetDeviceStatistics(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: [
      "analytics",
      "auth",
      "device-statistics",
      segment,
      startDate,
      endDate,
    ],
    queryFn: () => client.getDeviceStatistics(segment, startDate, endDate),
  });
}

export function useGetDailyActivityStats(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: [
      "analytics",
      "auth",
      "daily-activity",
      segment,
      startDate,
      endDate,
    ],
    queryFn: () => client.getDailyActivityStats(segment, startDate, endDate),
  });
}

export function useGetTutorialStats(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: ["analytics", "tutorial", "stats", segment, startDate, endDate],
    queryFn: () => client.getTutorialStats(segment, startDate, endDate),
  });
}

// Signup Analytics Queries
export function useGetSignupAnalytics(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
  groupBy: "day" | "week" | "month" | "hour" = "day",
) {
  return useQuery({
    queryKey: ["analytics", "signup", segment, startDate, endDate, groupBy],
    queryFn: () =>
      client.getSignupAnalytics(segment, startDate, endDate, groupBy),
  });
}

export function useGetSignupFunnel(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: ["analytics", "signup", "funnel", segment, startDate, endDate],
    queryFn: () => client.getSignupFunnel(segment, startDate, endDate),
  });
}

export function useGetSignupsBySegment(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: [
      "analytics",
      "signup",
      "by-segment",
      segment,
      startDate,
      endDate,
    ],
    queryFn: () => client.getSignupsBySegment(segment, startDate, endDate),
  });
}

export function useGetBookmarks(
  type: BookmarkTypeEnum,
  moduleId?: string,
  pageId?: string,
) {
  return useQuery({
    queryKey: ["bookmarks", type, moduleId, pageId],
    queryFn: () => client.getBookmarks(type, moduleId, pageId),
  });
}

export function useGetAllFeedback() {
  return useQuery({
    queryKey: ["feedback", "all"],
    queryFn: () => client.getAllFeedback(),
  });
}

export function useGetFeedbackByUser(userSub: string) {
  return useQuery({
    queryKey: ["feedback", "user", userSub],
    queryFn: () => client.getFeedbackByUser(userSub),
    enabled: !!userSub,
  });
}

export function useGetFeedbackStats() {
  return useQuery({
    queryKey: ["feedback", "stats"],
    queryFn: () => client.getFeedbackStats(),
  });
}

export function invalidateLessonFeedbackQueries(
  queryClient: QueryClient,
  lessonId: string,
) {
  void queryClient.invalidateQueries({
    queryKey: ["feedback", "lesson", lessonId],
  });
  void queryClient.invalidateQueries({
    queryKey: ["feedback", "lesson", "count", lessonId],
  });
  void queryClient.invalidateQueries({
    queryKey: ["feedback", "page", "count", lessonId],
  });
}

export function invalidatePageFeedbackQueries(
  queryClient: QueryClient,
  lessonId: string,
  pageNumber: number,
) {
  void queryClient.invalidateQueries({
    queryKey: ["feedback", "page", lessonId, pageNumber],
  });
  void queryClient.invalidateQueries({
    queryKey: ["feedback", "page", "count", lessonId, pageNumber],
  });
  void queryClient.invalidateQueries({
    queryKey: ["feedback", "lesson", "count", lessonId],
  });
}

export function useGetPageFeedback(lessonId: string, pageNumber: number) {
  return useQuery({
    queryKey: ["feedback", "page", lessonId, pageNumber],
    queryFn: () => client.getPageFeedback(lessonId, pageNumber),
    enabled: !!lessonId && pageNumber !== undefined,
  });
}

export function useGetLessonFeedback(lessonId: string) {
  return useQuery({
    queryKey: ["feedback", "lesson", lessonId],
    queryFn: () => client.getLessonFeedback(lessonId),
    enabled: !!lessonId,
  });
}

export function useGetLessonFeedbackCount(lessonId: string) {
  return useQuery({
    queryKey: ["feedback", "lesson", "count", lessonId],
    queryFn: () => client.getLessonFeedbackCount(lessonId),
    enabled: !!lessonId,
  });
}

export function useGetPageFeedbackCount(lessonId: string, pageNumber: number) {
  return useQuery({
    queryKey: ["feedback", "page", "count", lessonId, pageNumber],
    queryFn: () => client.getPageFeedbackCount(lessonId, pageNumber),
    enabled: !!lessonId && pageNumber !== undefined,
  });
}

export function useGetLegalCompliance(enabled: boolean = true) {
  return useQuery<LegalComplianceResponse>({
    queryKey: ["legal-compliance"],
    queryFn: () => client.checkLegalCompliance(),
    enabled,
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (
        error?.message?.includes("401") ||
        error?.message?.includes("unauthorized")
      ) {
        return false;
      }
      return failureCount < 3;
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
}

export function useGetTutorialStatus(enabled: boolean = true) {
  return useQuery<TutorialStatusResponse>({
    queryKey: ["tutorial-status"],
    queryFn: () => client.getTutorialStatus(),
    enabled,
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (
        error?.message?.includes("401") ||
        error?.message?.includes("unauthorized")
      ) {
        return false;
      }
      return failureCount < 3;
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
}

export function useGetToolboxUsageStats(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: ["analytics", "toolbox", "usage", segment, startDate, endDate],
    queryFn: () => client.getToolboxUsageStats(segment, startDate, endDate),
  });
}

export function useGetToolboxOpenEvents(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: ["analytics", "toolbox", "events", segment, startDate, endDate],
    queryFn: () => client.getToolboxOpenEvents(segment, startDate, endDate),
  });
}

export function useGetToolboxPageAddedEvents(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: [
      "analytics",
      "toolbox",
      "page-added",
      segment,
      startDate,
      endDate,
    ],
    queryFn: () =>
      client.getToolboxPageAddedEvents(segment, startDate, endDate),
  });
}

export function useGetToolboxPageOpenedEvents(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: [
      "analytics",
      "toolbox",
      "page-opened",
      segment,
      startDate,
      endDate,
    ],
    queryFn: () =>
      client.getToolboxPageOpenedEvents(segment, startDate, endDate),
  });
}

export function useGetVideoViews(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: ["analytics", "video", "views", segment, startDate, endDate],
    queryFn: () => client.getVideoViews(segment, startDate, endDate),
  });
}

export function useGetVideoViewsByTopicId(
  moduleId: string,
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [
      "analytics",
      "video",
      "views",
      "module",
      moduleId,
      segment,
      startDate,
      endDate,
    ],
    queryFn: () =>
      client.getVideoViewsByTopicId(moduleId, segment, startDate, endDate),
    enabled: !!moduleId && (options?.enabled ?? true),
  });
}

export function useGetVideoViewsByTopicAndLesson(
  moduleId: string,
  lessonId: string,
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [
      "analytics",
      "video",
      "views",
      "module",
      moduleId,
      "lesson",
      lessonId,
      segment,
      startDate,
      endDate,
    ],
    queryFn: () =>
      client.getVideoViewsByTopicAndLesson(
        moduleId,
        lessonId,
        segment,
        startDate,
        endDate,
      ),
    enabled: !!moduleId && !!lessonId && (options?.enabled ?? true),
  });
}

export function useGetLegalDates() {
  return useQuery({
    queryKey: ["legal-dates"],
    queryFn: () => client.getLegalDates(),
    staleTime: 5 * 60 * 1000, // 5 minutes - legal dates don't change often
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useGetAccessCodes(
  segment?: string,
  isActive?: boolean,
  expired?: boolean,
  search?: string,
) {
  return useQuery({
    queryKey: ["access-codes", segment, isActive, expired, search],
    queryFn: () => client.getAccessCodes(segment, isActive, expired, search),
  });
}

export function useGetAccessCode(id: string) {
  return useQuery({
    queryKey: ["access-codes", id],
    queryFn: () => client.getAccessCode(id),
    enabled: !!id,
  });
}

export function useGetAccessCodeAnalyticsByCode(accessCode: string) {
  return useQuery({
    queryKey: ["access-code-analytics", "code", accessCode],
    queryFn: () => client.getAccessCodeAnalyticsByCode(accessCode),
    enabled: !!accessCode,
  });
}

export function useGetAccessCodeAnalyticsById(id: string) {
  return useQuery({
    queryKey: ["access-code-analytics", "id", id],
    queryFn: () => client.getAccessCodeAnalyticsById(id),
    enabled: !!id,
  });
}

export function useGetAllAnnouncements(
  statusOrParams?: AnnouncementStatusFilter | AnnouncementFilters,
  enabled: boolean = true,
) {
  const params: AnnouncementFilters | undefined =
    typeof statusOrParams === "string"
      ? { status: statusOrParams }
      : statusOrParams;

  const queryKey = ["announcements", params];

  return useQuery({
    queryKey,
    queryFn: () => client.getAllAnnouncements(params),
    enabled: enabled,
    staleTime: params?.status === "active" ? 0 : 5 * 60 * 1000,
    refetchOnWindowFocus: params?.status === "active",
    refetchOnMount: params?.status === "active",
  });
}

// Admin dashboard hook - FORCES showDismissed to "true"
export function useGetAllAnnouncementsForAdmin(
  statusOrParams?: AnnouncementStatusFilter | AnnouncementFilters,
  enabled: boolean = true,
) {
  const baseParams: AnnouncementFilters | undefined =
    typeof statusOrParams === "string"
      ? { status: statusOrParams }
      : statusOrParams;

  // KEY: Force showDismissed to "true" for admin management view
  const params: AnnouncementFilters = {
    ...baseParams,
    showDismissed: "true",
  };

  const queryKey = ["announcements", "admin", params];

  return useQuery({
    queryKey,
    queryFn: () => client.getAllAnnouncements(params),
    enabled: enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
}

// User view hook - explicitly sets showDismissed to "false"
export function useGetActiveAnnouncementsForUser(enabled: boolean = true) {
  const params: AnnouncementFilters = {
    status: "active",
    showDismissed: "false", // Filter out dismissed for THIS user (admin or not)
    sort: "-priority,-createdAt",
  };

  return useGetAllAnnouncements(params, enabled);
}

// Keep other hooks unchanged
export function useGetAnnouncement(id: string) {
  return useQuery({
    queryKey: ["announcements", id],
    queryFn: () => client.getAnnouncement(id),
    enabled: !!id,
  });
}

export function useGetUserAnnouncementInteractions(enabled: boolean = true) {
  return useQuery({
    queryKey: ["announcements", "interactions"],
    queryFn: () => client.getUserAnnouncementInteractions(),
    enabled: enabled,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
}

export function useGetAccountDeletionStatus() {
  return useQuery({
    queryKey: ["accountDeletionStatus"],
    queryFn: () => client.getAccountDeletionStatus(),
  });
}

export function useGetUserDeletionStatus(userSub: string | null) {
  return useQuery({
    queryKey: ["userDeletionStatus", userSub],
    queryFn: () => {
      if (!userSub) throw new Error("userSub is required");
      return client.getUserDeletionStatus(userSub);
    },
    enabled: !!userSub,
  });
}

export function useGetErrorReports(
  segment: UserSegmentEnum[],
  search?: string,
  startDate?: string,
  endDate?: string,
  statusCodes?: number[],
  includeResolved: boolean = false,
  page: number = 1,
  limit: number = 20,
) {
  return useQuery({
    queryKey: [
      "errorReports",
      segment,
      search,
      startDate,
      endDate,
      statusCodes,
      includeResolved,
      page,
      limit,
    ],
    queryFn: () =>
      client.getErrorReports(
        segment,
        search,
        startDate,
        endDate,
        statusCodes,
        includeResolved,
        page,
        limit,
      ),
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useGetErrorReportsByStatusCode(
  segment: UserSegmentEnum[],
  search?: string,
  startDate?: string,
  endDate?: string,
  statusCodes?: number[],
  includeResolved: boolean = false,
) {
  return useQuery({
    queryKey: [
      "errorReportsByStatusCode",
      segment,
      search,
      startDate,
      endDate,
      statusCodes,
      includeResolved,
    ],
    queryFn: () =>
      client.getErrorReportsByStatusCode(
        segment,
        search,
        startDate,
        endDate,
        statusCodes,
        includeResolved,
      ),
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useUpdateErrorReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, resolved }: { id: string; resolved: boolean }) =>
      client.updateErrorReport(id, resolved),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["errorReports"] });
      queryClient.invalidateQueries({ queryKey: ["errorReportsByStatusCode"] });
    },
  });
}

export function useGetUserRoleAuditLogs(params?: {
  search?: string;
  limit?: number;
  skip?: number;
}) {
  return useQuery({
    queryKey: [
      "userRoleAuditLogs",
      params?.search,
      params?.limit,
      params?.skip,
    ],
    queryFn: () => rbacClient.fetchUserRoleAuditLogs(params),
    staleTime: 30000, // 30 seconds
  });
}

export function useGetProfileDefinitionsByTopic(topicId: string) {
  return useQuery<ToolboxProfileDefinitionI[], APIError>({
    queryKey: ["toolboxProfileDefinitions", topicId],
    queryFn: () => client.getProfileDefinitionsByTopic(topicId),
    enabled: !!topicId,
  });
}

export function useGetActiveProfileDefinition(topicId: string) {
  return useQuery<ToolboxProfileDefinitionI, APIError>({
    queryKey: ["toolboxProfileDefinitions", topicId, "active"],
    queryFn: () => client.getActiveProfileDefinitionByTopic(topicId),
    enabled: !!topicId,
  });
}

export function useGetProfileDefinition(definitionId: string) {
  return useQuery<ToolboxProfileDefinitionI, APIError>({
    queryKey: ["toolboxProfileDefinition", definitionId],
    queryFn: () => client.getProfileDefinition(definitionId),
    enabled: !!definitionId,
  });
}

export function useGetCheckinDefinitionsByTopic(topicId: string) {
  return useQuery<ToolboxCheckinDefinitionI[], APIError>({
    queryKey: ["toolboxCheckinDefinitions", topicId],
    queryFn: () => client.getCheckinDefinitionsByTopic(topicId),
    enabled: !!topicId,
  });
}

export function useGetActiveCheckinDefinition(topicId: string) {
  return useQuery<ToolboxCheckinDefinitionI, APIError>({
    queryKey: ["toolboxCheckinDefinitions", topicId, "active"],
    queryFn: () => client.getActiveCheckinDefinitionByTopic(topicId),
    enabled: !!topicId,
  });
}

export function useGetCheckinDefinitionById(definitionId: string) {
  return useQuery<ToolboxCheckinDefinitionI, APIError>({
    queryKey: ["toolboxCheckinDefinition", definitionId],
    queryFn: () => client.getCheckinDefinitionById(definitionId),
    enabled: !!definitionId,
  });
}

export function useGetToolboxTopicPlans(
  topicId: string,
  options?: { enabled?: boolean },
) {
  return useQuery<ToolboxPlanI[], APIError>({
    queryKey: ["toolboxTopicPlans", topicId],
    queryFn: () => client.getToolboxTopicPlans(topicId),
    enabled: !!topicId && (options?.enabled ?? true),
  });
}

export function useGetToolboxPlan(
  planId: string,
  options?: { enabled?: boolean },
) {
  return useQuery<ToolboxPlanI, APIError>({
    queryKey: ["toolboxPlan", planId],
    queryFn: () => client.getToolboxPlan(planId),
    enabled: !!planId && (options?.enabled ?? true),
    retry: (_, error) =>
      !(error instanceof APIError && error.statusCode === 404),
  });
}

export function useGetUserToolboxProfile(topicId: string) {
  return useQuery<UserToolboxProfileI, APIError>({
    queryKey: ["userToolboxProfile", topicId],
    queryFn: () => client.getUserToolboxProfile(topicId),
    enabled: !!topicId,
    retry: (_, error) =>
      !(error instanceof APIError && error.statusCode === 404),
  });
}
